import React, { useEffect, useState, createContext, useContext, useRef, useCallback } from 'react';
import { Session, AuthChangeEvent } from '@supabase/supabase-js';
import supabase from '../../services/supabase';
import { requestTeacherAccess } from '../../services/teacherAccess';

// Define user type with role information
type UserType = {
  uid: string;
  email: string;
  role: 'staff' | 'parent' | 'teacher';
  displayName?: string;
} | null;

// Auth context type
type AuthContextType = {
  user: UserType;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithSSO: (provider: 'google' | 'microsoft' | 'azure', queryParams?: Record<string, string>) => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
};

// Read the signed-in user's LD profile. Null when there is none (or the read failed).
async function loadProfile(userId: string): Promise<UserType | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }

    if (!data) return null;

    return {
      uid: data.id,
      email: data.email,
      role: data.role,
      displayName: data.display_name
    };
  } catch (err) {
    console.error('Error in fetchUserProfile:', err);
    return null;
  }
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserType>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use refs to track state without causing re-renders
  const isInitializedRef = useRef(false);
  const isMountedRef = useRef(true);

  // Fetch user profile data from profiles table
  const fetchUserProfile = useCallback(async (userId: string, accessToken?: string): Promise<UserType | null> => {
    const first = await loadProfile(userId);
    if (first || !accessToken) return first;
    // No LD profile: a current SCR Central teacher gets one at first sign-in (#425).
    if (!(await requestTeacherAccess(accessToken))) return null;
    return loadProfile(userId);
  }, []);

  // Handle auth state changes - single useEffect for everything
  useEffect(() => {
    isMountedRef.current = true;

    // Set up listener FIRST - this is important per Supabase docs
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, newSession: Session | null) => {
      if (!isMountedRef.current) return;

      // For INITIAL_SESSION, this is our main initialization path
      if (event === 'INITIAL_SESSION') {
        setSession(newSession);

        if (newSession?.user) {
          // Use setTimeout to avoid potential deadlock with Supabase client
          // This is a recommended pattern from Supabase docs
          setTimeout(async () => {
            if (!isMountedRef.current) return;
            const profile = await fetchUserProfile(newSession.user.id, newSession.access_token);
            if (isMountedRef.current) {
              if (profile) {
                setUser(profile);
              } else {
                // No profile — user not pre-registered by admin
                await supabase.auth.signOut();
                setUser(null);
                setError('No account found for this email. Please contact your administrator.');
              }
              setLoading(false);
            }
          }, 0);
        } else {
          setUser(null);
          setLoading(false);
        }
        isInitializedRef.current = true;
        return;
      }

      // For other events (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, etc.)
      setSession(newSession);

      if (newSession?.user) {
        // Use setTimeout to avoid potential deadlock
        setTimeout(async () => {
          if (!isMountedRef.current) return;
          const profile = await fetchUserProfile(newSession.user.id, newSession.access_token);
          if (isMountedRef.current) {
            if (profile) {
              setUser(profile);
            } else {
              await supabase.auth.signOut();
              setUser(null);
              setError('No account found for this email. Please contact your administrator.');
            }
          }
        }, 0);
      } else {
        setUser(null);
      }
    });

    // Safety timeout - if onAuthStateChange doesn't fire within 5 seconds, stop loading
    const safetyTimeout = setTimeout(() => {
      if (isMountedRef.current && !isInitializedRef.current) {
        console.warn('Auth initialization timeout - forcing loading to false');
        setLoading(false);
        isInitializedRef.current = true;
      }
    }, 5000);

    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
      clearTimeout(safetyTimeout);
    };
  }, [fetchUserProfile]);

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) throw signInError;

      if (data.user) {
        // Fetch the user's profile to get role information
        const profile = await fetchUserProfile(data.user.id, data.session?.access_token);
        if (!profile) {
          throw new Error('User profile not found. Please contact an administrator.');
        }
        setUser(profile);
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'Invalid email or password');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with SSO provider
  const signInWithSSO = async (provider: 'google' | 'microsoft' | 'azure', queryParams?: Record<string, string>) => {
    try {
      setError(null);
      setLoading(true);

      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: provider === 'microsoft' || provider === 'azure' ? 'azure' : 'google',
        options: {
          scopes: 'email profile openid',
          redirectTo: `${window.location.origin}/auth/callback`,
          ...(queryParams ? { queryParams } : {})
        }
      });

      if (signInError) throw signInError;

      // The redirect will happen automatically
      // When user returns, the onAuthStateChange listener will handle setting the user
    } catch (err: any) {
      console.error('SSO sign in error:', err);
      setError(err.message || 'SSO sign in failed');
      setLoading(false);
      throw err;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError(null);
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;

      setUser(null);
      setSession(null);
    } catch (err: any) {
      console.error('Sign out error:', err);
      setError('Error signing out');
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signInWithSSO,
        signOut,
        error
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
